import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import inventoryReportsService, {
  InventorySummary,
  TopSellingProduct,
  LowStockProduct,
  InventoryValuation,
  ProductMovement
} from '../services/inventoryReportsService';
import './InventoryReports.css';

const InventoryReports: React.FC = () => {
  const [summary, setSummary] = useState<InventorySummary | null>(null);
  const [activeTab, setActiveTab] = useState<'top-selling' | 'low-stock' | 'valuation' | 'movements'>('top-selling');
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [loading, setLoading] = useState(false);

  // Top Selling Products
  const [topSellingProducts, setTopSellingProducts] = useState<TopSellingProduct[]>([]);
  const [topSellingStartDate, setTopSellingStartDate] = useState('');
  const [topSellingEndDate, setTopSellingEndDate] = useState('');
  const [topSellingLimit, setTopSellingLimit] = useState(10);

  // Low Stock Products
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([]);

  // Inventory Valuation
  const [inventoryValuation, setInventoryValuation] = useState<InventoryValuation[]>([]);

  // Product Movements
  const [productMovements, setProductMovements] = useState<ProductMovement[]>([]);
  const [movementsStartDate, setMovementsStartDate] = useState('');
  const [movementsEndDate, setMovementsEndDate] = useState('');

  const loadSummary = async () => {
    try {
      setLoading(true);
      const data = await inventoryReportsService.getSummary();
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopSellingProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await inventoryReportsService.getTopSellingProducts(
        topSellingStartDate || undefined,
        topSellingEndDate || undefined,
        topSellingLimit
      );
      setTopSellingProducts(data);
    } catch (error) {
      console.error('Error loading top selling products:', error);
    } finally {
      setLoading(false);
    }
  }, [topSellingStartDate, topSellingEndDate, topSellingLimit]);

  useEffect(() => {
    loadSummary();
    loadTopSellingProducts();
  }, [loadTopSellingProducts]);

  const loadLowStockProducts = async () => {
    try {
      setLoading(true);
      const data = await inventoryReportsService.getLowStockProducts();
      setLowStockProducts(data);
    } catch (error) {
      console.error('Error loading low stock products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryValuation = async () => {
    try {
      setLoading(true);
      const data = await inventoryReportsService.getInventoryValuation();
      setInventoryValuation(data);
    } catch (error) {
      console.error('Error loading inventory valuation:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProductMovements = async () => {
    try {
      setLoading(true);
      const data = await inventoryReportsService.getProductMovements(
        movementsStartDate || undefined,
        movementsEndDate || undefined
      );
      setProductMovements(data);
    } catch (error) {
      console.error('Error loading product movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    switch (tab) {
      case 'top-selling':
        if (topSellingProducts.length === 0) loadTopSellingProducts();
        break;
      case 'low-stock':
        if (lowStockProducts.length === 0) loadLowStockProducts();
        break;
      case 'valuation':
        if (inventoryValuation.length === 0) loadInventoryValuation();
        break;
      case 'movements':
        if (productMovements.length === 0) loadProductMovements();
        break;
    }
  };

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const formatNumber = (value: number) => value.toLocaleString();

  return (
    <div className="inventory-reports-container">
      <h1>Reportes de Inventario</h1>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Productos</h3>
            <p className="summary-value">{formatNumber(summary.totalProducts)}</p>
          </div>
          <div className="summary-card">
            <h3>Valor Inventario</h3>
            <p className="summary-value">{formatCurrency(summary.totalValue)}</p>
          </div>
          <div className="summary-card">
            <h3>Ganancia Potencial</h3>
            <p className="summary-value">{formatCurrency(summary.totalProfit)}</p>
          </div>
          <div className="summary-card">
            <h3>Productos Bajo Stock</h3>
            <p className="summary-value warning">{formatNumber(summary.lowStockProducts)}</p>
          </div>
          <div className="summary-card">
            <h3>Sin Stock</h3>
            <p className="summary-value danger">{formatNumber(summary.outOfStockProducts)}</p>
          </div>
          <div className="summary-card">
            <h3>Días Promedio Stock</h3>
            <p className="summary-value">{summary.averageStockDays.toFixed(1)}</p>
          </div>
        </div>
      )}

      {/* Report Type Tabs */}
      <div className="report-tabs">
        <button
          className={activeTab === 'top-selling' ? 'active' : ''}
          onClick={() => handleTabChange('top-selling')}
        >
          Productos Más Vendidos
        </button>
        <button
          className={activeTab === 'low-stock' ? 'active' : ''}
          onClick={() => handleTabChange('low-stock')}
        >
          Bajo Stock
        </button>
        <button
          className={activeTab === 'valuation' ? 'active' : ''}
          onClick={() => handleTabChange('valuation')}
        >
          Valorización
        </button>
        <button
          className={activeTab === 'movements' ? 'active' : ''}
          onClick={() => handleTabChange('movements')}
        >
          Movimientos
        </button>
      </div>

      {/* View Mode Tabs */}
      <div className="view-mode-tabs">
        <button
          className={`view-tab ${viewMode === 'chart' ? 'active' : ''}`}
          onClick={() => setViewMode('chart')}
        >
          Gráfica
        </button>
        <button
          className={`view-tab ${viewMode === 'table' ? 'active' : ''}`}
          onClick={() => setViewMode('table')}
        >
          Tabla
        </button>
      </div>

      {/* Top Selling Products */}
      {activeTab === 'top-selling' && (
        <div className="report-section">
          <div className="report-header">
            <h2>Productos Más Vendidos</h2>
            <div className="filters">
              <input
                type="date"
                value={topSellingStartDate}
                onChange={(e) => setTopSellingStartDate(e.target.value)}
                placeholder="Fecha Inicio"
              />
              <input
                type="date"
                value={topSellingEndDate}
                onChange={(e) => setTopSellingEndDate(e.target.value)}
                placeholder="Fecha Fin"
              />
              <input
                type="number"
                value={topSellingLimit}
                onChange={(e) => setTopSellingLimit(Number(e.target.value))}
                min="5"
                max="50"
                placeholder="Límite"
              />
              <button onClick={loadTopSellingProducts}>Actualizar</button>
              <button onClick={() => inventoryReportsService.exportTopSellingProducts(
                topSellingStartDate || undefined,
                topSellingEndDate || undefined,
                topSellingLimit
              )}>
                Exportar Excel
              </button>
            </div>
          </div>

          {viewMode === 'chart' && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={topSellingProducts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
                <Bar dataKey="totalQuantitySold" fill="#8884d8" name="Unidades Vendidas" />
                <Bar dataKey="totalRevenue" fill="#82ca9d" name="Ingresos" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {viewMode === 'table' && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Unidades Vendidas</th>
                    <th>Ingresos</th>
                    <th>Stock Actual</th>
                  </tr>
                </thead>
                <tbody>
                  {topSellingProducts.map((product) => (
                    <tr key={product.productId}>
                      <td>{product.productName}</td>
                      <td>{product.categoryName}</td>
                      <td>{formatNumber(product.totalQuantitySold)}</td>
                      <td>{formatCurrency(product.totalRevenue)}</td>
                      <td>{formatNumber(product.currentStock)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Low Stock Products */}
      {activeTab === 'low-stock' && (
        <div className="report-section">
          <div className="report-header">
            <h2>Productos con Bajo Stock</h2>
            <div className="filters">
              <button onClick={loadLowStockProducts}>Actualizar</button>
              <button onClick={() => inventoryReportsService.exportLowStockProducts()}>
                Exportar Excel
              </button>
            </div>
          </div>

          {viewMode === 'chart' && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={lowStockProducts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="currentStock" fill="#ff7c7c" name="Stock Actual" />
                <Bar dataKey="minStockThreshold" fill="#ffc658" name="Stock Mínimo" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {viewMode === 'table' && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Stock Actual</th>
                    <th>Stock Mínimo</th>
                    <th>Déficit</th>
                    <th>Precio</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockProducts.map((product) => (
                    <tr key={product.productId} className={product.status === 'Critical' ? 'critical' : ''}>
                      <td>{product.productName}</td>
                      <td>{product.categoryName}</td>
                      <td>{formatNumber(product.currentStock)}</td>
                      <td>{formatNumber(product.minStockThreshold)}</td>
                      <td>{formatNumber(product.stockDeficit)}</td>
                      <td>{formatCurrency(product.price)}</td>
                      <td>
                        <span className={`status-badge ${product.status.toLowerCase()}`}>
                          {product.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Inventory Valuation */}
      {activeTab === 'valuation' && (
        <div className="report-section">
          <div className="report-header">
            <h2>Valorización de Inventario</h2>
            <div className="filters">
              <button onClick={loadInventoryValuation}>Actualizar</button>
              <button onClick={() => inventoryReportsService.exportInventoryValuation()}>
                Exportar Excel
              </button>
            </div>
          </div>

          {viewMode === 'chart' && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={inventoryValuation}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="totalCost" fill="#ff8042" name="Costo Total" />
                <Bar dataKey="totalValue" fill="#00c49f" name="Valor Total" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {viewMode === 'table' && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Stock</th>
                    <th>Precio Costo</th>
                    <th>Precio Venta</th>
                    <th>Costo Total</th>
                    <th>Valor Total</th>
                    <th>Margen %</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryValuation.map((item) => (
                    <tr key={item.productId}>
                      <td>{item.productName}</td>
                      <td>{item.categoryName}</td>
                      <td>{formatNumber(item.currentStock)}</td>
                      <td>{formatCurrency(item.costPrice)}</td>
                      <td>{formatCurrency(item.salePrice)}</td>
                      <td>{formatCurrency(item.totalCost)}</td>
                      <td>{formatCurrency(item.totalValue)}</td>
                      <td>{item.profitMargin.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Product Movements */}
      {activeTab === 'movements' && (
        <div className="report-section">
          <div className="report-header">
            <h2>Movimientos de Productos</h2>
            <div className="filters">
              <input
                type="date"
                value={movementsStartDate}
                onChange={(e) => setMovementsStartDate(e.target.value)}
                placeholder="Fecha Inicio"
              />
              <input
                type="date"
                value={movementsEndDate}
                onChange={(e) => setMovementsEndDate(e.target.value)}
                placeholder="Fecha Fin"
              />
              <button onClick={loadProductMovements}>Actualizar</button>
              <button onClick={() => inventoryReportsService.exportProductMovements(
                movementsStartDate || undefined,
                movementsEndDate || undefined
              )}>
                Exportar Excel
              </button>
            </div>
          </div>

          {viewMode === 'chart' && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={productMovements}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value: number) => value.toFixed(2)} />
                <Legend />
                <Bar dataKey="averageDailySales" fill="#8884d8" name="Promedio Ventas Diarias" />
                <Bar dataKey="daysOfStockRemaining" fill="#82ca9d" name="Días de Stock" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {viewMode === 'table' && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Stock Actual</th>
                    <th>Total Vendido</th>
                    <th>Promedio Ventas Diarias</th>
                    <th>Días de Stock</th>
                    <th>Última Venta</th>
                  </tr>
                </thead>
                <tbody>
                  {productMovements.map((movement) => (
                    <tr key={movement.productId}>
                      <td>{movement.productName}</td>
                      <td>{movement.categoryName}</td>
                      <td>{formatNumber(movement.currentStock)}</td>
                      <td>{formatNumber(movement.totalSold)}</td>
                      <td>{movement.averageDailySales.toFixed(2)}</td>
                      <td>{movement.daysOfStockRemaining.toFixed(1)}</td>
                      <td>{movement.lastSaleDate ? new Date(movement.lastSaleDate).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading && <div className="loading-overlay">Cargando...</div>}
    </div>
  );
};

export default InventoryReports;
